import { ReactNode, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Box, List, ListItem, ListItemIcon, Typography, useMediaQuery } from '@mui/material';
import { alpha, type Theme, useTheme } from '@mui/material/styles';
import { Icon } from '@malash-office/ui-components';
import { useAppContext } from '@/context/AppContext';
import { LOGOUT_PATH, NAV_ITEMS, NavItem } from './navConfig';
import FlyoutMenu from './FlyoutMenu';
import MobileNav from './MobileNav';
import { MOBILE_BOTTOM_NAV_INSET } from './mobileLayoutConstants';
import { AgentChatLauncher } from '@/components/agentChat';

const DRAWER_WIDTH = 95;
const SIDEBAR_GAP = 20;

const normalizePath = (path: string) => {
  const p = path.endsWith('/') ? path.slice(0, -1).toLowerCase() : path.toLowerCase();
  return p || '/';
};

const DashboardLayout = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout, hasPermission } = useAppContext();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selected = normalizePath(location.pathname);

  const contentPadding = isSmallScreen ? 16 : 24;

  const isItemVisible = (item: NavItem): boolean => {
    if (item.children) {
      return item.children.some(c => !c.permission || hasPermission(c.permission));
    }
    return !item.permission || hasPermission(item.permission);
  };

  const isItemSelected = (item: NavItem): boolean => {
    if (item.to) {
      return selected === normalizePath(item.to);
    }
    return !!item.children?.some(c => selected === normalizePath(c.to));
  };

  const openMenu = (index: number) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenIndex(index);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenIndex(null), 150);
  };

  const goTo = (to: string) => {
    setOpenIndex(null);
    if (to === LOGOUT_PATH) {
      logout();
      navigate('/login');
      return;
    }
    navigate(to);
  };

  // Personal area is reached via the profile block, not a rail icon (still shown in the mobile nav).
  const visibleItems = NAV_ITEMS.filter(item => isItemVisible(item) && !item.hideInDesktopRail);
  // The profile opens the personal area — now a child of "הגדרות" rather than a hidden leaf.
  const personalAreaTo = '/';

  // Shared style for the rounded icon tiles in the rail.
  const iconTileSx = (active: boolean) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 14,
    height: 14,
    p: '7px',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: 'common.white',
    backgroundColor: (theme: Theme) => (active ? theme.palette.common.white : alpha(theme.palette.common.white, 0.4)),
    boxSizing: 'content-box' as const
  });

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: 'grey.400',
        p: isSmallScreen ? 1 : 2,
        boxSizing: 'border-box',
        position: 'relative',
        overflow: isSmallScreen ? 'hidden' : 'visible'
      }}
    >
      {!isSmallScreen && openIndex !== null && visibleItems[openIndex]?.children && (
        <FlyoutMenu
          items={visibleItems[openIndex]!.children!.filter(c => !c.permission || hasPermission(c.permission))}
          drawerWidth={DRAWER_WIDTH}
          onNavigate={goTo}
          onMouseEnter={() => openMenu(openIndex)}
          onMouseLeave={scheduleClose}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          gap: isSmallScreen ? 0 : `${SIDEBAR_GAP}px`,
          pb: isSmallScreen ? `${MOBILE_BOTTOM_NAV_INSET}px` : 0,
          boxSizing: 'border-box'
        }}
      >
        {/* Right rail (first child → right side in RTL) — desktop only */}
        {!isSmallScreen && (
        <Box
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            gap: 1.25,
            height: '100%',
            borderRadius: '20px',
            backgroundColor: theme => alpha(theme.palette.common.white, 0.42),
            boxShadow: '0 4px 50px rgba(98, 112, 131, 0.10), inset 0 0 10px rgba(255, 255, 255, 0.55)',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          <List
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2.5,
              width: '100%',
              py: 1,
              // Only this list scrolls — keeps the profile section pinned and the page from overflowing.
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              direction: 'rtl',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 }
            }}
          >
            {visibleItems.map((item, index) => {
              const active = isItemSelected(item) || openIndex === index;
              return (
                <Box
                  key={item.label}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                  onMouseEnter={() => item.children && openMenu(index)}
                  onMouseLeave={() => item.children && scheduleClose()}
                >
                  <ListItem
                    onClick={() => item.to && goTo(item.to)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      p: 0,
                      cursor: 'pointer'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'unset', ...iconTileSx(active) }}>
                      <Icon icon={item.icon} size="small" color="primary.main" />
                    </ListItemIcon>
                    <Typography
                      color="primary"
                      fontWeight={600}
                      sx={{ textAlign: 'center', fontSize: 10, lineHeight: 1.15, mt: 0.5, maxWidth: DRAWER_WIDTH - 16 }}
                    >
                      {item.label}
                    </Typography>
                  </ListItem>
                </Box>
              );
            })}
          </List>

          {/* Profile (click → personal area). התנתקות is under ניהול in the menu. */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, width: '100%', flexShrink: 0 }}
          >
            {user && (
              <Box
                onClick={() => goTo(personalAreaTo)}
                title="אזור אישי"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  borderRadius: '10px',
                  p: 0.5,
                  '&:hover': { backgroundColor: theme => alpha(theme.palette.common.white, 0.5) }
                }}
              >
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
                  {user.firstName?.[0] || 'מ'}
                </Avatar>
                <Typography
                  variant="caption"
                  color="primary"
                  fontWeight={600}
                  sx={{ textAlign: 'center', lineHeight: 1.1 }}
                >
                  {user.firstName} {user.lastName}
                </Typography>
                {user.roleNames.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize: 10 }}>
                    {user.roleNames.join(', ')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
        )}

        {/* Content */}
        <Box
          sx={{
            flexGrow: 1,
            height: '100%',
            overflow: 'auto',
            borderRadius: '20px',
            backgroundColor: 'common.white',
            boxShadow: '0 4px 50px rgba(98, 112, 131, 0.10)',
            p: `${contentPadding}px`,
            boxSizing: 'border-box'
          }}
        >
          {children}
        </Box>
      </Box>

      {isSmallScreen && <MobileNav />}

      {/* Renders itself only for roles that hold `agent:use` — see AgentChatLauncher. */}
      <AgentChatLauncher />
    </Box>
  );
};

export default DashboardLayout;
